import {
  DiscountCodeGenerationStrategy,
  RedoDiscount,
  RedoDiscountConfiguration,
} from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountProvider,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import {
  BxgyTriggerType,
  DiscountCategory,
  DiscountSettingsType,
  DynamicRangeExpirationUnits,
} from "@redotech/redo-model/discount/discount-db-parser";
import { assertNever } from "@redotech/util/type";
import isInteger from "lodash/isInteger";
import { CustomerEligibility, ProductSelection } from "../discount-selector";

export interface DiscountValidationParams {
  discountName: string;
  discountAmount?: number;
  discountValueType: DiscountValueType;
  codeGenerationStrategy: DiscountGroupType;
  codeStrategyName: string;
  combineDiscountWith: {
    orderDiscounts: boolean;
    productDiscounts: boolean;
    shippingDiscount: boolean;
  };
  excludeOnSale: boolean;
  specifyCollections: boolean;
  specifyProducts: boolean;
  collectionIds: string[];
  productIds: string[];
  expiration: RedoDiscount["expiration"];
  minimumQuantity?: number;
  minimumSubtotal?: number;
  customerEligibility: CustomerEligibility;
  selectedCustomerIds: string[];
  limitToSegments?: string[];
  totalUsageLimit?: number;
  appliesOncePerCustomer: boolean;
  fullStartDateTime?: Date;
  existingDiscount?: RedoDiscount;
  existingDiscounts?: RedoDiscount[];
  category?: DiscountCategory;
  // Discount settings type
  discountSettingsType: DiscountSettingsType;
  // Free Shipping fields
  maximumShippingPrice?: number;
  freeShippingCountryCodes?: string[];
  // BxGy fields
  bxgyTriggerType?: BxgyTriggerType;
  bxgyTriggerQuantity?: number;
  bxgyTriggerAmount?: number;
  bxgyTriggerItemType?: "products" | "collections";
  bxgyTriggerProducts?: ProductSelection[];
  bxgyTriggerCollections?: string[];
  bxgyRewardItemType?: "products" | "collections";
  bxgyRewardProducts?: ProductSelection[];
  bxgyRewardCollections?: string[];
  bxgyRewardQuantity?: number;
  bxgyRewardDiscountType?: DiscountValueType;
  bxgyRewardDiscountValue?: number;
  bxgyUsesPerOrderLimit?: number;
  // Dynamic Range fields
  dynamicRangeMinPercentage?: number;
  dynamicRangeMaxPercentage?: number;
  dynamicRangeExpirationUnits?: DynamicRangeExpirationUnits;
  dynamicRangeExpirationValue?: number;
}

export function validateDiscountConfiguration(
  params: DiscountValidationParams,
): RedoDiscountConfiguration | string {
  const {
    discountName,
    discountAmount,
    discountValueType,
    codeGenerationStrategy,
    codeStrategyName,
    combineDiscountWith,
    excludeOnSale,
    specifyCollections,
    specifyProducts,
    collectionIds,
    productIds,
    expiration,
    minimumQuantity,
    minimumSubtotal,
    customerEligibility,
    selectedCustomerIds,
    limitToSegments,
    totalUsageLimit,
    appliesOncePerCustomer,
    fullStartDateTime,
    existingDiscount,
    existingDiscounts,
    category,
    discountSettingsType,
    maximumShippingPrice,
    freeShippingCountryCodes,
    bxgyTriggerType,
    bxgyTriggerQuantity,
    bxgyTriggerAmount,
    bxgyTriggerItemType,
    bxgyTriggerProducts,
    bxgyTriggerCollections,
    bxgyRewardItemType,
    bxgyRewardProducts,
    bxgyRewardCollections,
    bxgyRewardQuantity,
    bxgyRewardDiscountType,
    bxgyRewardDiscountValue,
    bxgyUsesPerOrderLimit,
    dynamicRangeMinPercentage,
    dynamicRangeMaxPercentage,
    dynamicRangeExpirationUnits,
    dynamicRangeExpirationValue,
  } = params;

  if (excludeOnSale && specifyCollections) {
    return "Invalid state";
  }

  if (existingDiscount) {
    if (
      codeGenerationStrategy !==
      existingDiscount.codeGenerationStrategy.strategy
    ) {
      return "You cannot change the type of a discount once it is created";
    }
    if (
      codeStrategyName.toLowerCase() !==
      existingDiscount.codeGenerationStrategy.code.toLowerCase()
    ) {
      return "You cannot change the code of a discount once it is created";
    }
  }

  // Static code uniqueness check
  if (codeGenerationStrategy === DiscountGroupType.STATIC) {
    const lowerCaseStrategyName = codeStrategyName.toLowerCase();
    const hasStaticNamingConflict = existingDiscounts?.some(
      (loadedDiscount) => {
        return (
          loadedDiscount.codeGenerationStrategy.strategy ===
            DiscountGroupType.STATIC &&
          loadedDiscount.codeGenerationStrategy.code.toLowerCase() ===
            lowerCaseStrategyName &&
          loadedDiscount._id !== existingDiscount?._id
        );
      },
    );
    if (hasStaticNamingConflict) {
      return "Static code names must be unique";
    }
  }

  // Basic field validations (common to all types)
  if (!discountName) {
    return "The discount must have a name";
  }

  if (!codeStrategyName) {
    return "The discount code is required";
  }

  if (minimumQuantity !== undefined && minimumQuantity < 0) {
    return "Minimum quantity must be greater than or equal to 0";
  }

  if (minimumSubtotal !== undefined && minimumSubtotal < 0) {
    return "Minimum purchase amount must be greater than or equal to 0";
  }

  if (totalUsageLimit !== undefined && totalUsageLimit < 0) {
    return "Total number of uses must be greater than or equal to 0";
  }

  if (
    expiration.expirationType === ExpirationType.EXPIRATION_DAYS &&
    (!expiration.days || expiration.days <= 0)
  ) {
    return "Number of days must be greater than 0";
  }

  if (minimumQuantity && minimumSubtotal) {
    return "Use one of minimum quantity or purchase amount, not both";
  }

  const now = new Date();
  if (!existingDiscount && fullStartDateTime && fullStartDateTime < now) {
    return "Start date/time cannot be in the past";
  }

  if (
    expiration.expirationType === ExpirationType.DATE &&
    expiration.date < now
  ) {
    return "Expiration date cannot be in the past";
  }

  if (
    fullStartDateTime &&
    expiration.expirationType === ExpirationType.DATE &&
    fullStartDateTime >= expiration.date
  ) {
    return "Expiration date must be after the start date";
  }

  // Type-specific validations
  switch (discountSettingsType) {
    case DiscountSettingsType.SHOPIFY_BASIC: {
      if (!discountAmount || discountAmount < 0) {
        return "The discount amount must be greater than 0";
      }

      if (
        discountValueType === DiscountValueType.PERCENTAGE &&
        discountAmount > 100
      ) {
        return "A percentage must be between 1 and 100";
      }

      if (
        discountValueType === DiscountValueType.PERCENTAGE &&
        !isInteger(discountAmount)
      ) {
        return "A percentage must be a whole number";
      }

      if (
        discountValueType === DiscountValueType.AMOUNT &&
        discountAmount > 1000
      ) {
        return "The discount amount must be less than $1000";
      }
      break;
    }
    case DiscountSettingsType.SHOPIFY_FREE_SHIPPING: {
      // Free shipping has fewer required fields
      break;
    }
    case DiscountSettingsType.SHOPIFY_BXGY: {
      // Check for trigger items (products or collections)
      const hasTriggerItems =
        (bxgyTriggerProducts && bxgyTriggerProducts.length > 0) ||
        (bxgyTriggerCollections && bxgyTriggerCollections.length > 0);
      if (!hasTriggerItems) {
        return "Please select at least one product or collection for 'Customer buys'";
      }
      // Check for reward items (products or collections)
      const hasRewardItems =
        (bxgyRewardProducts && bxgyRewardProducts.length > 0) ||
        (bxgyRewardCollections && bxgyRewardCollections.length > 0);
      if (!hasRewardItems) {
        return "Please select at least one product or collection for 'Customer gets'";
      }
      if (!bxgyRewardQuantity || bxgyRewardQuantity < 1) {
        return "Reward quantity must be at least 1";
      }
      if (
        bxgyRewardDiscountValue === undefined ||
        bxgyRewardDiscountValue < 0
      ) {
        return "Reward discount value must be 0 or greater";
      }
      if (
        bxgyRewardDiscountType === DiscountValueType.PERCENTAGE &&
        bxgyRewardDiscountValue > 100
      ) {
        return "Reward percentage must be between 0 and 100";
      }
      if (
        bxgyTriggerType === BxgyTriggerType.MINIMUM_QUANTITY &&
        (!bxgyTriggerQuantity || bxgyTriggerQuantity < 1)
      ) {
        return "Trigger quantity must be at least 1";
      }
      if (
        bxgyTriggerType === BxgyTriggerType.MINIMUM_AMOUNT &&
        (!bxgyTriggerAmount || bxgyTriggerAmount < 0)
      ) {
        return "Trigger amount must be 0 or greater";
      }
      break;
    }
    case DiscountSettingsType.DYNAMIC_RANGE: {
      if (
        dynamicRangeMinPercentage === undefined ||
        dynamicRangeMinPercentage < 0 ||
        dynamicRangeMinPercentage > 100
      ) {
        return "Minimum percentage must be between 0 and 100";
      }
      if (
        dynamicRangeMaxPercentage === undefined ||
        dynamicRangeMaxPercentage < 0 ||
        dynamicRangeMaxPercentage > 100
      ) {
        return "Maximum percentage must be between 0 and 100";
      }
      if (dynamicRangeMinPercentage > dynamicRangeMaxPercentage) {
        return "Minimum percentage must be less than or equal to maximum percentage";
      }
      if (!dynamicRangeExpirationUnits) {
        return "Expiration units are required";
      }
      if (!dynamicRangeExpirationValue || dynamicRangeExpirationValue <= 0) {
        return "Expiration value must be greater than 0";
      }
      break;
    }
    default:
      assertNever(discountSettingsType);
  }

  // Build the configuration
  const codeStrategy: DiscountCodeGenerationStrategy = (() => {
    switch (codeGenerationStrategy) {
      case DiscountGroupType.STATIC:
        return { strategy: DiscountGroupType.STATIC, code: codeStrategyName };
      case DiscountGroupType.DYNAMIC:
        return { strategy: DiscountGroupType.DYNAMIC, code: codeStrategyName };
      default:
        assertNever(codeGenerationStrategy);
    }
  })();

  // Build minimum requirement helper
  const buildMinimumRequirement = () => {
    if (minimumQuantity && minimumQuantity > 0) {
      return {
        type: DiscountMinRequirementType.MINIMUM_QUANTITY,
        minimum: minimumQuantity,
      };
    } else if (minimumSubtotal && minimumSubtotal > 0) {
      return {
        type: DiscountMinRequirementType.MINIMUM_SUBTOTAL,
        minimum: minimumSubtotal,
      };
    }
    return undefined;
  };

  // Build discount settings based on type
  const buildDiscountSettings =
    (): RedoDiscountConfiguration["discountSettings"] => {
      switch (discountSettingsType) {
        case DiscountSettingsType.SHOPIFY_BASIC:
          return {
            settingsType: DiscountSettingsType.SHOPIFY_BASIC,
            discountValueType,
            discountValueAmount: discountAmount!,
            combinesWith: combineDiscountWith,
            excludeItemsOnSale: excludeOnSale,
            appliesToCollections:
              specifyCollections && collectionIds.length > 0
                ? collectionIds
                : undefined,
            appliesToProducts:
              specifyProducts && productIds.length > 0 ? productIds : undefined,
            endsAt:
              expiration.expirationType === ExpirationType.DATE
                ? expiration.date
                : undefined,
            minimumRequirement: buildMinimumRequirement(),
            customerEligibility,
            limitToCustomers:
              customerEligibility === CustomerEligibility.SPECIFIC
                ? selectedCustomerIds
                : undefined,
            limitToSegments:
              customerEligibility === CustomerEligibility.SEGMENTS
                ? limitToSegments
                : undefined,
            totalUsageLimit:
              totalUsageLimit && totalUsageLimit > 0
                ? totalUsageLimit
                : undefined,
            appliesOncePerCustomer,
            startsAt:
              fullStartDateTime || existingDiscount?.discountSettings.startsAt,
          };
        case DiscountSettingsType.SHOPIFY_FREE_SHIPPING:
          return {
            settingsType: DiscountSettingsType.SHOPIFY_FREE_SHIPPING,
            combinesWith: combineDiscountWith,
            maximumShippingPrice,
            countryCodes:
              freeShippingCountryCodes && freeShippingCountryCodes.length > 0
                ? freeShippingCountryCodes
                : undefined,
            minimumRequirement: buildMinimumRequirement(),
            customerEligibility,
            limitToCustomers:
              customerEligibility === CustomerEligibility.SPECIFIC
                ? selectedCustomerIds
                : undefined,
            limitToSegments:
              customerEligibility === CustomerEligibility.SEGMENTS
                ? limitToSegments
                : undefined,
            totalUsageLimit:
              totalUsageLimit && totalUsageLimit > 0
                ? totalUsageLimit
                : undefined,
            appliesOncePerCustomer,
            startsAt:
              fullStartDateTime || existingDiscount?.discountSettings.startsAt,
            endsAt:
              expiration.expirationType === ExpirationType.DATE
                ? expiration.date
                : undefined,
          };
        case DiscountSettingsType.SHOPIFY_BXGY:
          return {
            settingsType: DiscountSettingsType.SHOPIFY_BXGY,
            combinesWith: combineDiscountWith,
            customerBuys: {
              triggerType: bxgyTriggerType || BxgyTriggerType.MINIMUM_QUANTITY,
              quantity:
                bxgyTriggerType === BxgyTriggerType.MINIMUM_QUANTITY
                  ? bxgyTriggerQuantity
                  : undefined,
              amount:
                bxgyTriggerType === BxgyTriggerType.MINIMUM_AMOUNT
                  ? bxgyTriggerAmount
                  : undefined,
              products:
                bxgyTriggerItemType === "products"
                  ? bxgyTriggerProducts?.map((p) => ({
                      id: p.id,
                      title: p.title,
                    }))
                  : undefined,
              collections:
                bxgyTriggerItemType === "collections"
                  ? bxgyTriggerCollections?.map((id) => ({ id }))
                  : undefined,
            },
            customerGets: {
              products:
                bxgyRewardItemType === "products"
                  ? bxgyRewardProducts?.map((p) => ({
                      id: p.id,
                      title: p.title,
                    }))
                  : undefined,
              collections:
                bxgyRewardItemType === "collections"
                  ? bxgyRewardCollections?.map((id) => ({ id }))
                  : undefined,
              quantity: bxgyRewardQuantity || 1,
              discountValueType:
                bxgyRewardDiscountType || DiscountValueType.PERCENTAGE,
              discountValue: bxgyRewardDiscountValue || 100,
            },
            usesPerOrderLimit: bxgyUsesPerOrderLimit,
            customerEligibility,
            limitToCustomers:
              customerEligibility === CustomerEligibility.SPECIFIC
                ? selectedCustomerIds
                : undefined,
            limitToSegments:
              customerEligibility === CustomerEligibility.SEGMENTS
                ? limitToSegments
                : undefined,
            totalUsageLimit:
              totalUsageLimit && totalUsageLimit > 0
                ? totalUsageLimit
                : undefined,
            appliesOncePerCustomer,
            startsAt:
              fullStartDateTime || existingDiscount?.discountSettings.startsAt,
            endsAt:
              expiration.expirationType === ExpirationType.DATE
                ? expiration.date
                : undefined,
          };
        case DiscountSettingsType.DYNAMIC_RANGE:
          return {
            settingsType: DiscountSettingsType.DYNAMIC_RANGE,
            minPercentage: dynamicRangeMinPercentage!,
            maxPercentage: dynamicRangeMaxPercentage!,
            expiration: {
              units: dynamicRangeExpirationUnits!,
              value: dynamicRangeExpirationValue!,
            },
            combinesWith: combineDiscountWith,
          };
        default:
          assertNever(discountSettingsType);
      }
    };

  const discountSettings: RedoDiscountConfiguration = {
    ...existingDiscount,
    name: discountName,
    provider: DiscountProvider.SHOPIFY,
    codeGenerationStrategy: codeStrategy,
    expiration,
    discountSettings: buildDiscountSettings(),
    ...(category ? { category } : {}),
  };
  return discountSettings;
}
